import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation } from 'react-router'

import { type Identity, Role } from '@ankor-io/common/types/account'
import type { UserWithConnectedAccounts } from '@ankor-io/common/types/awx'

import { useUserContext } from '@/providers/user/useUserContext'
import Loading from '@/ui-components/Loading'
import AddUserToConnectedAccountsModal from '@/views/components/Users/AddUserToConnectedAccountsModal'
import EditRoleModal from '@/views/components/Users/EditRoleModal'
import RemoveUserFromConnectedAccountsModal from '@/views/components/Users/RemoveUserFromConnectedAccountsModal'
import RemoveUserModal from '@/views/components/Users/RemoveUserModal'

import { ActiveConnectedAccountUsersTable } from './ActiveConnectedAccountUsersTable'
import { ActiveOrganizationUsersTable } from './ActiveOrganizationUsersTable'
import type { ConnectedAccountUser, OrganizationUser } from './types'

const ITEMS_PER_PAGE = 100

const ActiveUsers = () => {
  const { getAccessToken } = useKindeAuth()
  const location = useLocation()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)

  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUser[]>([])
  const [connectedAccountUsers, setConnectedAccountUsers] = useState<ConnectedAccountUser[]>([])

  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(null)
  const [selectedConnectedAccountUser, setSelectedConnectedAccountUser] = useState<ConnectedAccountUser | null>(null)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [removeModalOpen, setRemoveModalOpen] = useState(false)
  const [addUserToConnectedAccountsModalOpen, setAddUserToConnectedAccountsModalOpen] = useState(false)
  const [removeUserFromConnectedAccountsModalOpen, setRemoveUserFromConnectedAccountsModalOpen] = useState(false)

  const isOrganizationUserPage = !location.pathname.includes('vessel-users')

  // Effect to fetch users
  useEffect(() => {
    // get list of users for the organization
    const getUsers = async (): Promise<OrganizationUser[]> => {
      const token = await getAccessToken()
      try {
        const resp = await fetch(`/api/iam/org/${activeOrganizationId}/users?take=${ITEMS_PER_PAGE}&skip=0`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (resp.ok) {
          try {
            const data = (await resp.json()) as { result: { identity: Identity; roles: Role[] }[] }
            const organizationUsers: OrganizationUser[] = data.result.map((user) => ({
              id: user.identity.accountId,
              name: `${user.identity.firstName} ${user.identity.lastName}`,
              email: user.identity.email,
              role: user.roles.join(', '),
              connectedAccounts: [],
            }))
            return organizationUsers
          } catch (err) {
            console.error('Error parsing users response:', err)
          }
        }
        return []
      } catch (err) {
        console.error('Failed to fetch organization users: ', err)
        return []
      }
    }
    // get list of users and their connected accounts
    const getUserAccessToConnectedAccounts = async (): Promise<UserWithConnectedAccounts[]> => {
      const token = await getAccessToken()
      try {
        const resp = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-accounts/user-access?take=${ITEMS_PER_PAGE}&skip=0`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        if (resp.ok) {
          try {
            const data = (await resp.json()) as {
              usersWithConnectedAccounts: UserWithConnectedAccounts[]
            }
            const userWithConnectedAccounts = data.usersWithConnectedAccounts
            return userWithConnectedAccounts
          } catch (err) {
            console.log('Error parsing connected accounts response: ', err)
          }
        }
        return []
      } catch (err) {
        console.error('Failed to fetch connected accounts: ', err)
        return []
      }
    }
    // get list of users for the organization and their connected accounts
    const getOrganizationUsers = async (): Promise<void> => {
      if (!activeOrganizationId) {
        return
      }
      setIsLoading(true)
      // TODO: these 2 api calls should be combined into one call if we want to support pagination
      const [promiseOrganizationUsers, promiseUsersWithConnectedAccounts] = await Promise.allSettled([
        getUsers(),
        getUserAccessToConnectedAccounts(),
      ])
      if (promiseOrganizationUsers.status === 'fulfilled' && promiseUsersWithConnectedAccounts.status === 'fulfilled') {
        const organizationUsers: OrganizationUser[] = promiseOrganizationUsers.value
        const usersWithConnectedAccounts: UserWithConnectedAccounts[] = promiseUsersWithConnectedAccounts.value
        setOrganizationUsers(
          organizationUsers.map((user) => {
            return {
              ...user,
              connectedAccounts:
                usersWithConnectedAccounts
                  .find((connectedUser) => connectedUser.user.id === user.id)
                  ?.connectedAccounts.map((connectedAccount) => connectedAccount.id) || [],
            }
          }),
        )
      } else {
        setOrganizationUsers([])
      }
      setIsLoading(false)
    }
    // get list of users for the active connected account
    const getConnectedAccountUsers = async (): Promise<void> => {
      if (!activeOrganizationId || !activeConnectedAccountId) {
        return
      }
      setIsLoading(true)
      try {
        const resp = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/users?take=${ITEMS_PER_PAGE}&skip=0`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )
        if (resp.ok) {
          try {
            const data = (await resp.json()) as { result: { identity: Identity; roles: Role[] }[] }
            const connectedAccountUsers: ConnectedAccountUser[] = data.result.map((user) => ({
              id: user.identity.accountId,
              name: `${user.identity.firstName} ${user.identity.lastName}`,
              email: user.identity.email,
              role: user.roles.join(', '),
            }))
            setConnectedAccountUsers(connectedAccountUsers)
          } catch (err) {
            console.error('Error parsing connected account users response:', err)
          }
        } else {
          setConnectedAccountUsers([])
        }
      } catch (err) {
        console.error('Failed to fetch connected account users: ', err)
        setConnectedAccountUsers([])
      }
      setIsLoading(false)
    }
    if (isOrganizationUserPage) {
      getOrganizationUsers()
    } else {
      getConnectedAccountUsers()
    }
  }, [activeConnectedAccountId, activeOrganizationId, getAccessToken, isOrganizationUserPage])

  const openEditUserModal = (user: OrganizationUser) => {
    setSelectedUser(user)
    setEditModalOpen(true)
  }

  const openRemoveUserModal = (user: OrganizationUser) => {
    setSelectedUser(user)
    setRemoveModalOpen(true)
  }

  const openAddUserToConnectedAccountsModal = (user: OrganizationUser) => {
    setSelectedUser(user)
    setAddUserToConnectedAccountsModalOpen(true)
  }

  const openRemoveUserFromConnectedAccountsModal = (user: OrganizationUser) => {
    setSelectedUser(user)
    setRemoveUserFromConnectedAccountsModalOpen(true)
  }

  const openRemoveUserFromSpecificConnectedAccountModal = (user: ConnectedAccountUser) => {
    setSelectedConnectedAccountUser(user)
    setRemoveUserFromConnectedAccountsModalOpen(true)
  }

  const handleSaveUserRole = async (_userId: string, role: Role) => {
    const updateUserRole = async () => {
      const res = await fetch(`/api/iam/org/${activeOrganizationId}/users/${_userId}/roles`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify({
          roles: [role],
        }),
      })

      setEditModalOpen(false)
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('You are not authorized to update this user role')
        }

        throw new Error('Failed to update user role')
      }

      setOrganizationUsers((prevUsers) => prevUsers.map((user) => (user.id === _userId ? { ...user, role } : user)))
    }

    toast.promise(updateUserRole(), {
      loading: 'Updating user role...',
      success: 'User role updated successfully',
      error: (error) => `${error.message}`,
    })
  }

  const handleRemoveUser = async (userId: string) => {
    const removeUser = async () => {
      const res = await fetch(`/api/iam/org/${activeOrganizationId}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
      })

      setRemoveModalOpen(false)
      if (!res.ok) {
        throw new Error('Failed to remove user')
      }

      setOrganizationUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId))
    }

    toast.promise(removeUser(), {
      loading: 'Removing user...',
      success: 'User removed successfully',
      error: 'Failed to remove user',
    })
  }

  const handleAddUserToConnectedAccount = async (userId: string, connectedAccountIds: string[]) => {
    const addUserToConnectedAccount = async () => {
      const token = await getAccessToken()

      const res = await fetch(`/api/pay/org/${activeOrganizationId}/connected-accounts/user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          connectedAccountIds,
        }),
      })

      if (!res.ok) {
        throw new Error(
          `Failed to add user to ${connectedAccountIds.length} vessel${connectedAccountIds.length > 1 ? 's' : ''}`,
        )
      }

      setAddUserToConnectedAccountsModalOpen(false)
      setOrganizationUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? { ...user, connectedAccounts: [...user.connectedAccounts, ...connectedAccountIds] }
            : user,
        ),
      )
    }

    toast.promise(addUserToConnectedAccount(), {
      loading: 'Adding user to vessels...',
      success: `User added to ${connectedAccountIds.length} vessel${connectedAccountIds.length > 1 ? 's' : ''} successfully`,
      error: (error) => `${error.message}`,
    })
  }

  const handleRemoveUserFromConnectedAccounts = async (userId: string, connectedAccountIds: string[]) => {
    const removeUserFromConnectedAccounts = async () => {
      const token = await getAccessToken()
      const res = await fetch(`/api/pay/org/${activeOrganizationId}/connected-accounts/user`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          connectedAccountIds,
        }),
      })

      if (!res.ok) {
        throw new Error('Failed to remove user from vessels')
      }

      setRemoveUserFromConnectedAccountsModalOpen(false)
      setOrganizationUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                connectedAccounts: user.connectedAccounts.filter(
                  (accountId) => !connectedAccountIds.includes(accountId),
                ),
              }
            : user,
        ),
      )
    }

    toast.promise(removeUserFromConnectedAccounts(), {
      loading: 'Removing user from vessels...',
      success: `User removed from ${connectedAccountIds.length} vessel${connectedAccountIds.length > 1 ? 's' : ''} successfully`,
      error: 'Failed to remove user from vessels',
    })
  }

  return (
    <>
      {isOrganizationUserPage ? (
        <ActiveOrganizationUsersTable
          users={organizationUsers}
          openEditUserModal={openEditUserModal}
          openAddUserToConnectedAccountsModal={openAddUserToConnectedAccountsModal}
          openRemoveUserFromConnectedAccountsModal={openRemoveUserFromConnectedAccountsModal}
          openRemoveUserModal={openRemoveUserModal}
        />
      ) : (
        <ActiveConnectedAccountUsersTable
          users={connectedAccountUsers}
          openRemoveUserFromConnectedAccountsModal={openRemoveUserFromSpecificConnectedAccountModal}
        />
      )}

      {isLoading ? (
        <div className="flex h-full items-center justify-center py-12">
          <Loading />
        </div>
      ) : (
        ((isOrganizationUserPage && organizationUsers.length === 0) ||
          (!isOrganizationUserPage && connectedAccountUsers.length === 0)) && (
          <div className="flex h-full items-center justify-center py-12">
            <p className="text-sm text-gray-600 dark:text-gray-400">No active users</p>
          </div>
        )
      )}

      {isOrganizationUserPage && (
        <EditRoleModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          user={selectedUser}
          onSubmit={handleSaveUserRole}
        />
      )}

      {isOrganizationUserPage && (
        <RemoveUserModal
          isOpen={removeModalOpen}
          onClose={() => setRemoveModalOpen(false)}
          user={selectedUser}
          onSubmit={handleRemoveUser}
        />
      )}

      {isOrganizationUserPage && (
        <AddUserToConnectedAccountsModal
          isOpen={addUserToConnectedAccountsModalOpen}
          onClose={() => setAddUserToConnectedAccountsModalOpen(false)}
          user={selectedUser}
          existingConnectedAccountIds={selectedUser?.connectedAccounts || []}
          onSubmit={handleAddUserToConnectedAccount}
        />
      )}

      {isOrganizationUserPage ? (
        <RemoveUserFromConnectedAccountsModal
          isOpen={removeUserFromConnectedAccountsModalOpen}
          onClose={() => setRemoveUserFromConnectedAccountsModalOpen(false)}
          user={selectedUser}
          connectedAccountIds={selectedUser?.connectedAccounts || []}
          onSubmit={handleRemoveUserFromConnectedAccounts}
        />
      ) : (
        activeConnectedAccountId && (
          <RemoveUserFromConnectedAccountsModal
            isOpen={removeUserFromConnectedAccountsModalOpen}
            onClose={() => setRemoveUserFromConnectedAccountsModalOpen(false)}
            user={selectedConnectedAccountUser}
            connectedAccountIds={[activeConnectedAccountId]}
            onSubmit={handleRemoveUserFromConnectedAccounts}
          />
        )
      )}
    </>
  )
}

export default ActiveUsers
